/**
 * The Recruiter filter, once.
 *
 * Three screens narrow by who owns a row — the Daily Calls Report, the
 * Interested list and the Joining Tracker — and each had built its own copy.
 * They had already drifted: two asked the users endpoint for inactive accounts
 * and one did not, so the seventy-odd names the importer created were offered
 * on two pages and silently missing from the third. One component, one query
 * key, one answer.
 *
 * Grouped rather than filtered. Those accounts own most of the imported rows,
 * so removing them would put that work out of reach of the filter entirely;
 * what was wrong with the list was its length and its flatness, not its
 * contents. See lib/recruiters for how the two groups are told apart.
 *
 * The caller keeps its own visibility gate — this renders whenever it is
 * rendered, and the pages decide who sees a filter over other people's work.
 */
import { useQuery } from '@tanstack/react-query';
import { client } from '../api/client';
import { groupRecruiters, recruiterSuffix } from '../lib/recruiters';
import { titleCaseName } from '../lib/titleCase';
import type { Page, User } from '../types';

export default function RecruiterFilter({
  value,
  onChange,
  className = '',
}: {
  value: string;
  onChange: (value: string) => void;
  /** Width, which is the only thing the three call sites disagreed about. */
  className?: string;
}) {
  const staff = useQuery<Page<User>>({
    // One key across all three screens, so the list is fetched once and the
    // three cannot disagree about it even transiently.
    queryKey: ['users', 'recruiter-filter'],
    queryFn: async () =>
      (
        await client.get('/api/v1/admin/users', {
          // Inactive included deliberately: a recruiter who has left is still
          // the recruiter on the rows they made, and every account the
          // importer created is deactivated by design.
          params: { limit: 200, includeInactive: true },
        })
      ).data,
  });

  const { current, historical } = groupRecruiters(staff.data?.items ?? []);

  return (
    <label className={`text-xs text-gray-600 ${className}`}>
      Recruiter
      <select
        className="input mt-1 py-1 text-xs"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {/* Outside both groups: it is not a person, it is the absence of a
            filter. */}
        <option value="">Everyone</option>
        <optgroup label="Recruiters">
          {current.map((person) => (
            <option key={person.id} value={person.id}>
              {titleCaseName(person.fullName)}
              {recruiterSuffix(person)}
            </option>
          ))}
        </optgroup>
        {/* Only when there are any. An empty group heading on a company that
            has never imported a sheet is a label explaining nothing. */}
        {historical.length > 0 && (
          <optgroup label="From imported sheets">
            {historical.map((person) => (
              <option key={person.id} value={person.id}>
                {titleCaseName(person.fullName)}
              </option>
            ))}
          </optgroup>
        )}
      </select>
    </label>
  );
}
